import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatLegacyPaginator as MatPaginator } from '@angular/material/legacy-paginator';
import { merge, of as observableOf, Subject } from 'rxjs';
import {
  catchError,
  map,
  startWith,
  switchMap,
  takeUntil,
} from 'rxjs/operators';
import { FilterEnabledComponent } from 'src/app/helpers/filter-enabled.component';
import { FilterStoreService } from 'src/app/services/filter-store.service';
import {
  PAGE_SIZE_OPTIONS,
  PageIndexService,
} from 'src/app/services/page-index-service';
import {
  BulkPriceHeaders,
  PrintProductionDto,
} from '../../../../../model/print-production/print-production-dto';
import { PrintProductionRequestService } from '../service/print-production-request.service';
import { EditPrintProductionModalComponent } from '../edit-print-production-modal/edit-print-production-modal.component';
import Pageable from '../../../../../model/pageable';
import {
  DimensionType,
  MaterialType,
  NbColorsType,
  PrintMethodType,
  PrintProductTypes,
} from '../../../../../model/print-production/print-product-enums';
import { SnackService } from '../../../../../services/snack.service';
import { MatDialog } from '@angular/material/dialog';

@Component({
  selector: 'app-print-production-product',
  templateUrl: './print-production-product.component.html',
  styleUrls: ['./print-production-product.component.scss'],
})
export class PrintProductionProductComponent
  extends FilterEnabledComponent
  implements OnInit, OnDestroy
{
  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;

  totalItems = 0;
  pageSizeOptions = PAGE_SIZE_OPTIONS;
  loading = true;
  printProductionItems: PrintProductionDto[] = [];

  typeOptions: { key: string; value: string }[] =
    this.mapEnumToChoices(PrintProductTypes);
  dimensionOptions: { key: string; value: string }[] =
    this.mapEnumToChoices(DimensionType);
  printMethodOptions: { key: string; value: string }[] =
    this.mapEnumToChoices(PrintMethodType);

  private reload = new Subject<void>();
  private destroy = new Subject<void>();

  constructor(
    protected filterStoreService: FilterStoreService,
    protected pageIndexService: PageIndexService,
    private printProductionRequestService: PrintProductionRequestService,
    private dialog: MatDialog,
    private snackService: SnackService
  ) {
    super(
      [
        {
          key: 'id',
          strictEquality: true,
          numeric: true,
          active: false,
        },
        {
          key: 'type',
          value: '',
          strictEquality: true,
          startWith: true,
          active: false,
        },
        {
          key: 'dimensions',
          value: '',
          strictEquality: true,
          startWith: true,
          active: false,
        },
        {
          key: 'printMethod',
          value: '',
          strictEquality: true,
          startWith: true,
          active: false,
        },
      ],
      filterStoreService,
      pageIndexService
    );
  }

  ngOnInit(): void {
    this.initPaginator();
    this.loadData();
  }

  ngOnDestroy(): void {
    this.destroy.next();
    this.destroy.complete();
    super.ngOnDestroy();
  }

  private initPaginator(): void {
    this.paginator.pageIndex = this.initPageIndex;
    this.paginator.pageSize = this.initPageSize;
  }

  private loadData(): void {
    merge(this.paginator.page, this.filtersChanged, this.reload)
      .pipe(
        startWith({}),
        switchMap(() => {
          this.loading = true;
          this.updatePageIndexAndSize();
          const pageable: Pageable = {
            page: this.paginator.pageIndex,
            size: this.paginator.pageSize,
            sort: '',
          };
          return this.printProductionRequestService
            .getAllPrintProductions(
              this.generateFilterString(),
              pageable,
              false
            )
            .pipe(
              takeUntil(this.destroy),
              catchError(() =>
                observableOf({ totalItems: 0, printProductionItems: [] })
              )
            );
        }),
        map((response) => {
          this.totalItems = response.totalItems;
          return response.printProductionItems;
        })
      )
      .subscribe({
        next: (items) => {
          this.printProductionItems = items;
          this.loading = false;
          if (items.length === 0 && this.paginator.pageIndex > 0) {
            this.resetPagination();
          }
        },
        error: () => {
          this.loading = false;
          this.snackService.showError('Error loading print production items');
        },
      });
  }

  private updatePageIndexAndSize(): void {
    this.pageIndexService.setPageIndex(
      this.getLocalStorageKey(),
      this.paginator.pageIndex
    );
    this.pageIndexService.setPageSize(
      this.getLocalStorageKey(),
      this.paginator.pageSize
    );
  }

  private resetPagination(): void {
    this.pageIndexService.setPageIndex(this.getLocalStorageKey(), 0);
    this.paginator.pageIndex = 0;
    this.reload.next();
  }

  createNewPrintProduction(): void {
    this.openDetail(new PrintProductionDto());
  }

  editItem(item: PrintProductionDto): void {
    this.openDetail(item);
  }

  private openDetail(item: PrintProductionDto) {
    const dialogRef = this.dialog.open(EditPrintProductionModalComponent, {
      data: item,
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.reload.next();
      }
    });
  }

  deleteItem(item: PrintProductionDto): void {
    this.printProductionRequestService
      .deletePrintProduction(item.id)
      .subscribe({
        next: () => {
          this.snackService.showSuccess('Drukwerk item successvol verwijderd');
          this.reloadData();
        },
        error: (error) => {
          console.error('Error deleting print production item:', error);
          this.snackService.showError('Error deleting print production item');
        },
      });
  }

  private reloadData(): void {
    if (
      this.printProductionItems.length === 1 &&
      this.paginator.pageIndex > 0
    ) {
      this.paginator.pageIndex = 0;
    }
    this.reload.next();
  }

  protected getLocalStorageKey(): string {
    return 'PrintProductionProductComponent';
  }

  getBulkPrice(item: PrintProductionDto, bulkNumber: number): number | null {
    const bulkPrice = item.bulkPrices.find(
      (bp) => bp.bulkNumber === bulkNumber
    );
    return bulkPrice ? bulkPrice.unitPrice : null;
  }

  getEnumDisplayValue(enumMap: any, value: string): string {
    return enumMap[value] || value;
  }

  // Mapping enum to dropdown options
  mapEnumToChoices(enumType: object) {
    return Object.entries(enumType).map(([key, value]) => ({
      key,
      value,
    }));
  }

  protected readonly PrintProductionType = PrintProductTypes;
  protected readonly MaterialType = MaterialType;
  protected readonly DimensionsType = DimensionType;
  protected readonly PrintMethodType = PrintMethodType;
  protected readonly NbColorsType = NbColorsType;
  protected readonly BulkPriceHeaders = BulkPriceHeaders;
}
